import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiFileText, FiSearch } from "react-icons/fi"; // FiEdit3 대신 FiFileText 사용
import { formAPI } from "../../../../../services/api";
import "./11SubjectList.css";
import { useSearchParams } from "react-router-dom";

const VIP11Read = ({ projectId = "neuro-icu11" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subjects, setSubjects] = useState([]); // 전체 데이터 저장
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [userValue, setUserValue] = useState(null);
  const [filteredSubjects, setFilteredSubjects] = useState([]); // 검색 필터링된 데이터
  const [paginatedSubjects, setPaginatedSubjects] = useState([]); // 페이지네이션된 데이터

  const ITEMS_PER_PAGE = 20; // SubjectList와 동일하게 변경
  const SEARCH_FIELDS = ["hos_id", "data_id"]; // 검색 필드 정의

  // URL 쿼리 파라미터 관리
  const currentParams = {
    search: searchParams.get("search") || "",
    page: parseInt(searchParams.get("page")) || 1,
  };

  // 쿼리 파라미터 업데이트 함수
  const updateQueryParams = (updates) => {
    const newParams = { ...currentParams, ...updates };
    setSearchParams(
      Object.entries(newParams)
        .filter(([_, value]) => value !== null && value !== "")
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    );
  };

  // 검색어 입력 핸들러
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // 검색 버튼 클릭 핸들러
  const handleSearch = () => {
    updateQueryParams({ search: searchInput, page: 1 }); // 검색 시 첫 페이지로
  };

  // 검색 입력에서 Enter 키 처리
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    updateQueryParams({ page: newPage });
  };

  // 전체 데이터 조회 - VIP11 백엔드 API 사용 (user_value=999로 모든 데이터 조회)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setErrorDetails(null);

        console.log("[11Read] 전체 데이터 조회 시작 (Master 권한으로):", {
          projectId,
          userValue: 999, // 모든 데이터 조회를 위해 999로 고정
        });

        // VIP11 백엔드 API를 사용한 전체 데이터 조회 (Master 권한)
        const vip11ApiUrl = "https://product.idea2m.com/api/vip11/get-data";
        const response = await fetch(vip11ApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            search: "", // 검색어 없이 전체 데이터 조회
            searchFields: SEARCH_FIELDS,
            userValue: 999, // Master 권한으로 모든 데이터 조회
          }),
        });

        if (!response.ok) {
          throw new Error(
            `API 호출 실패: ${response.status} ${response.statusText}`
          );
        }

        const responseData = await response.json();

        if (responseData.success) {
          setSubjects(responseData.rows); // 전체 데이터 저장
          setTotalCount(responseData.totalCount);

          console.log("[11Read] 전체 데이터 로드 완료:", {
            totalRows: responseData.rows.length,
            userValue: responseData.userValue,
            filteredByUserValue: responseData.filteredByUserValue,
            allUniqueUserIds: [
              ...new Set(responseData.rows.map((row) => row.user_id)),
            ],
          });
        } else {
          throw new Error(responseData.message || "데이터 조회 실패");
        }
      } catch (err) {
        console.error("[11Read] 데이터 조회 중 오류:", err);
        setErrorDetails({
          step: err.response?.data?.step,
          message: err.response?.data?.message,
          error: err.response?.data?.error,
          sql: err.response?.data?.error?.sql,
          sqlMessage: err.response?.data?.error?.sqlMessage,
        });
        setError(err.message || "데이터를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]); // 검색어와 페이지 의존성 제거

  // 클라이언트 사이드 검색 필터링
  useEffect(() => {
    let filtered = subjects;

    // 검색어가 있는 경우 필터링
    if (currentParams.search && currentParams.search.trim()) {
      const searchTerm = currentParams.search.toLowerCase().trim();
      filtered = subjects.filter((subject) => {
        return SEARCH_FIELDS.some((field) => {
          const value = subject.form_data?.[field] || subject[field];
          return value && value.toString().toLowerCase().includes(searchTerm);
        });
      });
    }

    setFilteredSubjects(filtered);

    console.log("[11Read] 클라이언트 필터링 완료:", {
      totalSubjects: subjects.length,
      filteredSubjects: filtered.length,
      searchTerm: currentParams.search,
    });
  }, [subjects, currentParams.search]);

  // 클라이언트 사이드 페이지네이션
  useEffect(() => {
    const startIndex = (currentParams.page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginated = filteredSubjects.slice(startIndex, endIndex);

    setPaginatedSubjects(paginated);

    console.log("[11Read] 페이지네이션 적용:", {
      currentPage: currentParams.page,
      startIndex,
      endIndex,
      paginatedCount: paginated.length,
      totalPages: Math.ceil(filteredSubjects.length / ITEMS_PER_PAGE),
    });
  }, [filteredSubjects, currentParams.page]);

  // user_value 조회
  useEffect(() => {
    if (!projectId) return;

    (async () => {
      try {
        const res = await formAPI.getUserValue(projectId);
        const val = res.userValue ?? res.user_value ?? null;
        setUserValue(val);
        console.log("[11Read] 현재 user_value:", val); // 디버깅용 로그
      } catch (err) {
        console.error("사용자 정보 조회 실패:", err);
      }
    })();
  }, [projectId]);

  // 의사 권한 체크 (user_value의 1의 자리가 짝수인 경우) + 관리자(999) 권한
  const isDoctor =
    userValue &&
    (userValue === 999 || Number(String(userValue).slice(-1)) % 2 === 0);

  // 접근 제한 메시지 컴포넌트
  const UnauthorizedMessage = () => (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        zIndex: 100,
      }}
    >
      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: "2rem",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          textAlign: "center",
          maxWidth: "400px",
        }}
      >
        <div
          style={{
            fontSize: "64px",
            color: "#dc3545",
            marginBottom: "1rem",
          }}
        >
          ⚠️
        </div>
        <h2 style={{ color: "#dc3545", marginBottom: "1rem" }}>접근 제한</h2>
        <p style={{ color: "#666", lineHeight: "1.5" }}>
          이 페이지는 의사 권한이 필요합니다.
          <br />
          권한이 필요한 경우 관리자에게 문의하세요.
          <br />
        </p>
      </div>
    </div>
  );

  const handleReadSubject = (subject) => {
    console.log("[VIP11Read] Navigating to read with:", {
      dataId: subject.data_id,
      formData: subject.form_data,
    });

    navigate(`/ecrf/read/view/${subject.data_id}`, {
      state: {
        projectId: "11",
        formData: subject.form_data,
      },
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Complete: { color: "#10b981", bg: "#d1fae5" },
      "In process": { color: "#f59e0b", bg: "#fef3c7" },
      "Not started": { color: "#ef4444", bg: "#fee2e2" },
    };

    const config = statusConfig[status] || statusConfig["Not started"];

    return (
      <span
        className="status-badge"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        {status || "Not started"}
      </span>
    );
  };

  const renderCellContent = (subject, column) => {
    const value = subject.form_data[column.id] || subject[column.id];

    switch (column.type) {
      case "read": // edit에서 read로 변경
        return (
          <button
            className="action-btn read-btn" // edit-btn에서 read-btn으로 변경
            onClick={() => handleReadSubject(subject)}
            title="Read"
            style={{
              backgroundColor: "#e9ecef", // 읽기 버튼의 스타일 변경
              color: "#495057",
            }}
          >
            <FiFileText /> {/* FiEdit3에서 FiFileText로 변경 */}
          </button>
        );
      case "status":
        return getStatusBadge(value);
      case "date":
        return value ? new Date(value).toLocaleDateString() : "-";
      default:
        return value || "-";
    }
  };

  // 고정 컬럼 정의
  const columns = [
    {
      id: "__read__", // __edit__ 에서 __read__로 변경
      label: "Read", // Edit 에서 Read로 변경
      type: "read", // edit 에서 read로 변경
      sortable: false,
      width: "80px",
    },
    {
      id: "hos_id",
      label: "HOS_ID",
      type: "text",
      sortable: true,
      width: "120px",
    },
    {
      id: "data_id",
      label: "DATA_ID",
      type: "text",
      sortable: true,
      width: "120px",
    },
    {
      id: "initial_status",
      label: "Initial",
      type: "status",
      sortable: true,
      width: "100px",
    },
    {
      id: "icu_status",
      label: "ICU",
      type: "status",
      sortable: true,
      width: "100px",
    },
    {
      id: "daily_status",
      label: "Daily",
      type: "status",
      sortable: true,
      width: "100px",
    },
    {
      id: "additional_daily_status",
      label: "Additional Daily",
      type: "status",
      sortable: true,
      width: "120px",
    },
    {
      id: "early_outcome_status",
      label: "Early Outcome",
      type: "status",
      sortable: true,
      width: "120px",
    },
    {
      id: "hosp_discharge_status",
      label: "Hospital Discharge",
      type: "status",
      sortable: true,
      width: "140px",
    },
    {
      id: "surgeries_status",
      label: "Surgeries",
      type: "status",
      sortable: true,
      width: "100px",
    },
    {
      id: "Final_status",
      label: "Final Outcome",
      type: "status",
      sortable: true,
      width: "120px",
    },
  ];

  // 개선된 페이지네이션 컴포넌트 (11SubjectList와 동일)
  const Pagination = () => {
    const filteredTotalCount = filteredSubjects.length;
    const totalPages = Math.ceil(filteredTotalCount / ITEMS_PER_PAGE);
    const currentPage = currentParams.page;

    // 표시할 페이지 번호 범위 계산 (최대 10개 버튼)
    const maxPageButtons = Math.min(10, totalPages);
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    // 시작 페이지 조정
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    // 10페이지 단위 이동 계산
    const currentGroup = Math.ceil(currentPage / 10);
    const totalGroups = Math.ceil(totalPages / 10);
    const prevGroupStart = Math.max(1, (currentGroup - 2) * 10 + 1);
    const nextGroupStart = Math.min(totalPages, currentGroup * 10 + 1);

    if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          전체 {filteredTotalCount}건 / {totalPages} 페이지 중 {currentPage}{" "}
          페이지
        </div>
        <div className="pagination">
          {/* 10페이지 단위 이동 버튼들 */}
          <div className="pagination-group-navigation">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="pagination-button group-nav"
              title="첫 페이지"
            >
              ≪
            </button>
            <button
              onClick={() => handlePageChange(prevGroupStart)}
              disabled={currentGroup === 1}
              className="pagination-button group-nav"
              title="이전 10페이지"
            >
              ‹
            </button>
          </div>

          {/* 페이지 번호 버튼들 */}
          <div className="pagination-numbers">
            {Array.from(
              { length: endPage - startPage + 1 },
              (_, i) => startPage + i
            ).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`pagination-button ${
                  pageNum === currentPage ? "active" : ""
                }`}
              >
                {pageNum}
              </button>
            ))}
          </div>

          {/* 10페이지 단위 이동 버튼들 */}
          <div className="pagination-group-navigation">
            <button
              onClick={() => handlePageChange(nextGroupStart)}
              disabled={currentGroup === totalGroups}
              className="pagination-button group-nav"
              title="다음 10페이지"
            >
              ›
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="pagination-button group-nav"
              title="마지막 페이지"
            >
              ≫
            </button>
          </div>
        </div>

        {/* 기존 네비게이션 (단일 페이지 이동) */}
        <div className="pagination-single-navigation">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button single-nav"
          >
            이전
          </button>
          <span className="current-page-indicator">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="pagination-button single-nav"
          >
            다음
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="vip-subject-list-container">
        <div className="loading-message">
          <div className="spinner"></div>
          <p>환자 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vip-subject-list-container">
        <div className="error-message">
          <h3>오류가 발생했습니다</h3>
          <p>{error}</p>
          {errorDetails && (
            <div className="error-details">
              <h4>상세 오류 정보:</h4>
              <ul>
                {errorDetails.step && <li>발생 단계: {errorDetails.step}</li>}
                {errorDetails.message && (
                  <li>오류 메시지: {errorDetails.message}</li>
                )}
                {errorDetails.sqlMessage && (
                  <li>SQL 오류: {errorDetails.sqlMessage}</li>
                )}
                {errorDetails.sql && <li>실행 쿼리: {errorDetails.sql}</li>}
                {errorDetails.error &&
                  typeof errorDetails.error === "object" && (
                    <li>
                      추가 정보:
                      <pre>{JSON.stringify(errorDetails.error, null, 2)}</pre>
                    </li>
                  )}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="vip-subject-list-container"
      style={{ position: "relative", minHeight: "100vh" }}
    >
      {!isDoctor && <UnauthorizedMessage />}
      {isDoctor && (
        <>
          <div className="filter-section">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder={`${SEARCH_FIELDS.join(", ")} 검색...`}
                value={searchInput}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
              />
              <button
                onClick={handleSearch}
                className="search-button"
                title="검색"
              >
                <FiSearch className="search-button-icon" />
              </button>
            </div>
          </div>

          <div className="table-container">
            <table className="subject-table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.id}
                      className={column.sortable ? "sortable" : ""}
                      style={{ width: column.width }}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedSubjects.map((subject) => (
                  <tr key={subject.id}>
                    {columns.map((column) => (
                      <td key={column.id}>
                        {column.id === "__read__"
                          ? renderCellContent(subject, column)
                          : renderCellContent(subject, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination />

          {paginatedSubjects.length === 0 && !loading && (
            <div className="empty-state">
              <p>
                {filteredSubjects.length === 0
                  ? "조건에 맞는 환자가 없습니다."
                  : "현재 페이지에 표시할 데이터가 없습니다."}
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  marginTop: "16px",
                  padding: "8px 16px",
                  backgroundColor: "#1976D2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
                </svg>
                새로고침
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VIP11Read;
